// frontend/src/pages/MyAds.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import p2pAxios from '../p2pAxios';
import { useNavigate } from 'react-router-dom';

const badge = {
  active: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-800/50',
  inactive: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-slate-700 dark:text-gray-200 dark:border-slate-600',
};

const typeBadge = {
  buy: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800/50',
  sell: 'bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-300 dark:border-indigo-800/50',
};

const fmtINR = (n) => Number(n || 0).toLocaleString('en-IN');
const fmtDate = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { hour12: true }) : '—';

// ---- expiry helpers (keep in sync with backend defaults/rules) ----
const DEFAULT_EXPIRE_MIN = 45; // backend default
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

// Always resolve to a concrete number: ad value (15–180) or platform default
const resolveExpireMinutes = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? clamp(Math.round(n), 15, 180) : DEFAULT_EXPIRE_MIN;
};

// Format minutes as a compact label (time only)
const fmtExpiry = (mins) => {
  const m = Number(mins);
  if (!Number.isFinite(m)) return `${DEFAULT_EXPIRE_MIN} min`;
  if (m % 60 === 0) return `${m / 60}h`;
  if (m > 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
  return `${m} min`;
};

const PAGE_SIZE = 12;

export default function MyAds() {
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);

  // Mobile/desktop helpers (UI only; does not change logic)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 640px)');
    const onChange = () => setIsMobile(!!mql.matches);
    onChange();
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);

  // Collapsible filters on mobile
  const [showFilters, setShowFilters] = useState(false);
  useEffect(() => {
    // Always show filters on desktop
    if (!isMobile) setShowFilters(true);
    // Default hidden on mobile
    if (isMobile) setShowFilters(false);
  }, [isMobile]);

  // Per-ad accordion open state (mobile only)
  const [openMap, setOpenMap] = useState({});
  const toggleOpen = (id) => {
    setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Smooth scroll to the opened item (mobile)
  const cardRefs = useRef({});
  const scrollIntoViewIfNeeded = (id) => {
    if (!isMobile) return;
    const el = cardRefs.current?.[id];
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  // Filters / sort / pagination
  const [q, setQ] = useState('');
  const [coinFilter, setCoinFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');     // ALL | buy | sell
  const [statusFilter, setStatusFilter] = useState('ALL'); // ALL | active | inactive
  const [methodFilter, setMethodFilter] = useState('ALL'); // ALL | method
  const [sortKey, setSortKey] = useState('updatedDesc');   // updatedDesc | createdDesc | priceDesc | priceAsc
  const [page, setPage] = useState(1);

  const fetchMyAds = async () => {
    setLoading(true);
    try {
      const res = await p2pAxios.get('/ads/my');
      setAds(res.data.ads || []);
    } catch (err) {
      console.error('Error fetching my ads:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMyAds();
  }, []);

  // Derived options for filters
  const allCoins = useMemo(
    () => Array.from(new Set(ads.map(a => (a.coin || 'USDT').toUpperCase()))).sort(),
    [ads]
  );
  const allMethods = useMemo(() => {
    const s = new Set();
    ads.forEach(a => (Array.isArray(a.paymentMethods) ? a.paymentMethods : []).forEach(m => s.add(m)));
    return Array.from(s).sort();
  }, [ads]);

  // Filtering + sorting
  const filtered = useMemo(() => {
    let arr = [...ads];

    // Query: match ID tail, coin, or status/type text
    const qnorm = q.trim().toLowerCase();
    if (qnorm) {
      arr = arr.filter(a => {
        const id = String(a._id || '').toLowerCase();
        const coin = String(a.coin || '').toLowerCase();
        const status = String(a.status || '').toLowerCase();
        const type = String(a.type || '').toLowerCase();
        return (
          id.includes(qnorm) ||
          coin.includes(qnorm) ||
          status.includes(qnorm) ||
          type.includes(qnorm)
        );
      });
    }

    if (coinFilter !== 'ALL') arr = arr.filter(a => (a.coin || '').toUpperCase() === coinFilter);
    if (typeFilter !== 'ALL') arr = arr.filter(a => String(a.type || '').toLowerCase() === typeFilter);
    if (statusFilter !== 'ALL') arr = arr.filter(a => String(a.status || '').toLowerCase() === statusFilter);
    if (methodFilter !== 'ALL')
      arr = arr.filter(a => (Array.isArray(a.paymentMethods) ? a.paymentMethods : []).includes(methodFilter));

    // Sort
    arr.sort((a, b) => {
      if (sortKey === 'updatedDesc') {
        return new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0);
      }
      if (sortKey === 'createdDesc') {
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }
      if (sortKey === 'priceDesc') return Number(b.price || 0) - Number(a.price || 0);
      if (sortKey === 'priceAsc') return Number(a.price || 0) - Number(b.price || 0);
      return 0;
    });

    return arr;
  }, [ads, q, coinFilter, typeFilter, statusFilter, methodFilter, sortKey]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageClamped = Math.min(Math.max(1, page), totalPages);
  const pageSlice = useMemo(() => {
    const start = (pageClamped - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, pageClamped]);

  // Reset to page 1 when filters/sorting change
  useEffect(() => {
    setPage(1);
  }, [q, coinFilter, typeFilter, statusFilter, methodFilter, sortKey]);

  const deactivateAd = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this ad?')) return;
    try {
      await p2pAxios.post(`/ads/deactivate/${id}`);
      fetchMyAds();
    } catch (err) {
      console.error('Error deactivating ad:', err);
    }
  };

  const activateAd = async (id) => {
    if (!window.confirm('Are you sure you want to activate this ad?')) return;
    try {
      await p2pAxios.post(`/ads/activate/${id}`);
      fetchMyAds();
    } catch (err) {
      console.error('Error activating ad:', err);
    }
  };

  const deleteAd = async (id) => {
    if (!window.confirm('Are you sure you want to permanently delete this ad?')) return;
    try {
      await p2pAxios.delete(`/ads/delete/${id}`);
      fetchMyAds();
    } catch (err) {
      console.error('Error deleting ad:', err);
    }
  };

  const editAd = (id) => navigate(`/p2p/edit-ad/${id}`);

  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
    } catch {}
  };

  return (
    // Page background now pure white
    <div className="min-h-screen bg-white dark:bg-slate-950 md:py-8 py-4 md:px-4 px-3">
      <div className="max-w-6xl mx-auto">
        {/* Header card */}
        <div className="bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 md:rounded-2xl rounded-xl md:shadow-lg shadow border-t-4 border-blue-500 md:p-6 p-3 md:mb-6 mb-4 overflow-hidden">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between md:gap-3 gap-2">
            <div>
              <h2 className="md:text-2xl text-xl font-bold text-blue-800 dark:text-blue-300">My P2P Ads</h2>
              <p className="text-gray-600 dark:text-gray-300 md:text-base text-sm">
                {filtered.length} result{filtered.length === 1 ? '' : 's'} • {ads.length} total
              </p>
            </div>
            <div className="flex items-center md:gap-3 gap-2">
              {/* Mobile: Filters toggle */}
              {isMobile && (
                <button
                  type="button"
                  onClick={() => setShowFilters((s) => !s)}
                  className="md:hidden px-3 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 text-gray-800 dark:text-gray-200 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-xs"
                >
                  {showFilters ? 'Hide Filters' : 'Show Filters'}
                </button>
              )}
              <button
                onClick={fetchMyAds}
                disabled={loading}
                className="md:px-4 px-3 md:py-2 py-1.5 rounded-lg border border-blue-200 dark:border-slate-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-slate-800 md:text-sm text-xs"
              >
                {loading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          </div>

          {/* Filters (collapsible on mobile) */}
          {showFilters && (
            <div className="md:mt-4 mt-3 grid grid-cols-1 md:grid-cols-12 md:gap-3 gap-2">
              <div className="md:col-span-4">
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by ID/coin/type/status…"
                  className="w-full border rounded-lg md:px-3 px-3 md:py-2 py-2 text-sm focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-slate-600"
                />
              </div>
              <div className="md:col-span-2">
                <select
                  value={coinFilter}
                  onChange={(e) => setCoinFilter(e.target.value)}
                  className="w-full border rounded-lg md:px-3 px-3 md:py-2 py-2 text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-slate-600"
                >
                  <option value="ALL">All Coins</option>
                  {allCoins.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full border rounded-lg md:px-3 px-3 md:py-2 py-2 text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-slate-600"
                >
                  <option value="ALL">All Types</option>
                  <option value="buy">Buy</option>
                  <option value="sell">Sell</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full border rounded-lg md:px-3 px-3 md:py-2 py-2 text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-slate-600"
                >
                  <option value="ALL">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <select
                  value={methodFilter}
                  onChange={(e) => setMethodFilter(e.target.value)}
                  className="w-full border rounded-lg md:px-3 px-3 md:py-2 py-2 text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-slate-600"
                >
                  <option value="ALL">All Methods</option>
                  {allMethods.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-3">
                <select
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                  className="w-full border rounded-lg md:px-3 px-3 md:py-2 py-2 text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-slate-600"
                >
                  <option value="updatedDesc">Sort: Updated (newest)</option>
                  <option value="createdDesc">Sort: Created (newest)</option>
                  <option value="priceDesc">Sort: Price (high → low)</option>
                  <option value="priceAsc">Sort: Price (low → high)</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <p className="text-gray-600 dark:text-gray-300">Loading…</p>
        ) : !pageSlice.length ? (
          <p className="text-gray-600 dark:text-gray-300">No ads match your filters.</p>
        ) : (
          <div className="space-y-3 md:space-y-5">
            {pageSlice.map((ad) => {
              const coin = (ad.coin || 'USDT').toUpperCase();
              const methods = Array.isArray(ad.paymentMethods) ? ad.paymentMethods : [];
              const statusStyle = badge[ad.status] || badge.inactive;
              const typeStyle = typeBadge[ad.type] || typeBadge.buy;

              const expireMins = resolveExpireMinutes(ad?.expireMinutes);
              const expireLabel = fmtExpiry(expireMins);

              const isOpen = !isMobile || !!openMap[ad._id];

              return (
                <div
                  key={ad._id}
                  ref={(el) => {
                    if (el) cardRefs.current[ad._id] = el;
                  }}
                  className="border border-gray-200 dark:border-slate-700 md:rounded-xl rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 md:shadow-sm shadow overflow-hidden"
                >
                  {/* Summary header (tap to expand on mobile) */}
                  <button
                    type="button"
                    onClick={() => {
                      if (!isMobile) return;
                      toggleOpen(ad._id);
                      setTimeout(() => scrollIntoViewIfNeeded(ad._id), 80);
                    }}
                    className={`w-full text-left md:cursor-default flex items-start justify-between gap-3 md:p-5 p-3 ${
                      isMobile ? 'active:bg-blue-50/50 dark:active:bg-slate-800/60' : ''
                    }`}
                    aria-expanded={isOpen}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-md text-[11px] md:text-xs border ${typeStyle}`}>
                          {ad.type?.toUpperCase() || '—'}
                        </span>
                        <span className="text-gray-900 dark:text-gray-50 md:font-semibold font-semibold md:text-base text-sm truncate">
                          ₹{fmtINR(ad.price)} <span className="font-normal text-gray-600 dark:text-gray-300">/ {coin}</span>
                        </span>
                        <span className={`px-2 py-0.5 rounded-md text-[11px] md:text-xs border ${statusStyle}`}>
                          {ad.status || 'inactive'}
                        </span>
                        <span className="px-2 py-0.5 rounded-md text-[11px] md:text-xs border bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800/50">
                          {expireLabel}
                        </span>
                      </div>

                      <div className="mt-1 flex items-center gap-2 text-[11px] md:text-xs text-gray-500 dark:text-gray-400">
                        <span className="font-mono truncate">ID: {ad._id}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            copy(ad._id);
                          }}
                          className="text-blue-600 dark:text-blue-300 hover:underline"
                        >
                          Copy
                        </button>
                      </div>
                    </div>

                    {/* Chevron (mobile only) */}
                    {isMobile && (
                      <div className="pt-1">
                        <span
                          className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 transition ${
                            isOpen ? 'rotate-180' : ''
                          }`}
                          aria-hidden="true"
                        >
                          ▾
                        </span>
                      </div>
                    )}
                  </button>

                  {/* Details (collapsible on mobile; always visible on desktop) */}
                  {isOpen && (
                    <div className="border-t border-gray-200 dark:border-slate-800 md:p-5 p-3 pt-3">
                      {/* Actions */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {ad.status === 'active' ? (
                          <button
                            onClick={() => deactivateAd(ad._id)}
                            className="bg-red-500 text-white px-3 py-1.5 rounded-lg hover:bg-red-600 text-xs md:text-sm"
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button
                            onClick={() => activateAd(ad._id)}
                            className="bg-green-500 text-white px-3 py-1.5 rounded-lg hover:bg-green-600 text-xs md:text-sm"
                          >
                            Activate
                          </button>
                        )}
                        <button
                          onClick={() => editAd(ad._id)}
                          className="bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 text-xs md:text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteAd(ad._id)}
                          className="bg-slate-700 text-white px-3 py-1.5 rounded-lg hover:bg-slate-800 text-xs md:text-sm"
                        >
                          Delete
                        </button>
                      </div>

                      {/* Body grid */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 md:gap-4">
                        {/* Basics */}
                        <div className="bg-blue-50/40 dark:bg-slate-800/60 border border-blue-100 dark:border-slate-700 rounded-lg p-3 md:p-4">
                          <h4 className="text-[12px] md:text-sm font-semibold text-blue-800 dark:text-blue-300 mb-1.5 md:mb-2">Basics</h4>
                          <p className="text-[12px] md:text-sm text-gray-700 dark:text-gray-200">
                            <strong>Coin:</strong> {coin}
                          </p>
                          <p className="text-[12px] md:text-sm text-gray-700 dark:text-gray-200">
                            <strong>Price:</strong> ₹{fmtINR(ad.price)}
                          </p>
                          <p className="text-[12px] md:text-sm text-gray-700 dark:text-gray-200">
                            <strong>Per-trade Expiry:</strong> {expireLabel}
                          </p>
                          <p className="text-[12px] md:text-sm text-gray-700 dark:text-gray-200">
                            <strong>Payment Methods:</strong> {methods.length ? methods.join(', ') : '—'}
                          </p>
                        </div>

                        {/* Limits */}
                        <div className="bg-blue-50/40 dark:bg-slate-800/60 border border-blue-100 dark:border-slate-700 rounded-lg p-3 md:p-4">
                          <h4 className="text-[12px] md:text-sm font-semibold text-blue-800 dark:text-blue-300 mb-1.5 md:mb-2">Limits</h4>
                          <p className="text-[12px] md:text-sm text-gray-700 dark:text-gray-200">
                            <strong>Min:</strong> ₹{fmtINR(ad.minLimit)}
                          </p>
                          <p className="text-[12px] md:text-sm text-gray-700 dark:text-gray-200">
                            <strong>Max:</strong> ₹{fmtINR(ad.maxLimit)}
                          </p>
                        </div>

                        {/* Meta */}
                        <div className="bg-blue-50/40 dark:bg-slate-800/60 border border-blue-100 dark:border-slate-700 rounded-lg p-3 md:p-4">
                          <h4 className="text-[12px] md:text-sm font-semibold text-blue-800 dark:text-blue-300 mb-1.5 md:mb-2">Meta</h4>
                          <p className="text-[12px] md:text-sm text-gray-700 dark:text-gray-200">
                            <strong>Status:</strong> {ad.status}
                          </p>
                          <p className="text-[12px] md:text-sm text-gray-700 dark:text-gray-200">
                            <strong>Created:</strong> {fmtDate(ad.createdAt)}
                          </p>
                          <p className="text-[12px] md:text-sm text-gray-700 dark:text-gray-200">
                            <strong>Updated:</strong> {fmtDate(ad.updatedAt)}
                          </p>
                        </div>
                      </div>

                      {(ad.terms || ad.autoReply) && (
                        <div className="mt-3 md:mt-4 grid grid-cols-1 md:grid-cols-2 gap-2.5 md:gap-4">
                          {ad.terms && (
                            <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg p-3 md:p-4">
                              <h4 className="text-[12px] md:text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5 md:mb-2">
                                Terms &amp; Conditions
                              </h4>
                              <div className="text-[12px] md:text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap">
                                {ad.terms}
                              </div>
                            </div>
                          )}
                          {ad.autoReply && (
                            <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg p-3 md:p-4">
                              <h4 className="text-[12px] md:text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5 md:mb-2">
                                Auto-Reply (on trade start)
                              </h4>
                              <div className="text-[12px] md:text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap">
                                {ad.autoReply}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="md:mt-6 mt-4 flex flex-wrap items-center justify-center md:gap-2 gap-1.5">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pageClamped === 1}
              className="md:px-3 px-3 md:py-1 py-1 rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 disabled:opacity-50 md:text-sm text-xs"
            >
              ‹ Prev
            </button>
            {Array.from({ length: totalPages }).map((_, i) => {
              const n = i + 1;
              const active = n === pageClamped;
              return (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  className={`md:px-3 px-3 md:py-1 py-1 rounded-lg border md:text-sm text-xs ${
                    active
                      ? 'bg-blue-600 text-white border-blue-700'
                      : 'bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700'
                  }`}
                >
                  {n}
                </button>
              );
            })}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={pageClamped === totalPages}
              className="md:px-3 px-3 md:py-1 py-1 rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 disabled:opacity-50 md:text-sm text-xs"
            >
              Next ›
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
